/**
 * Helpers shared by the rep and the admin leave screens.
 *
 * A row carries its status flat as `status_id` / `status_name`. `api/v1/leave-status`
 * returns 1 قيد المراجعة, 2 موافقة, 3 رفض — those ids drive the classification,
 * and a status outside them is matched on its name so a status added later
 * still colours and locks correctly.
 */

export const STATUS_KIND = {
  pending: "pending",
  approved: "approved",
  rejected: "rejected",
};

const BY_ID = {
  1: STATUS_KIND.pending,
  2: STATUS_KIND.approved,
  3: STATUS_KIND.rejected,
};

const HINTS = {
  // rejected is matched first: "لم تتم الموافقة" also contains "موافق".
  rejected: ["reject", "refus", "denied", "cancel", "مرفوض", "رفض", "ملغ"],
  approved: ["approve", "accept", "confirm", "موافق", "مقبول", "قبول"],
  pending: ["pending", "wait", "review", "قيد", "انتظار", "مراجعة", "معلق"],
};

/** `{ id, name }` for a row, or for an entry of the leave-status list. */
export const getRowStatus = (row) => {
  if (!row) return null;
  if (row.status_id) return { id: row.status_id, name: row.status_name };
  return row.id ? { id: row.id, name: row.name } : null;
};

export const getStatusKind = (status) => {
  const kind = BY_ID[status?.id];
  if (kind) return kind;
  const label = (status?.name ?? "").toLowerCase();
  return (
    ["rejected", "approved", "pending"].find((k) =>
      HINTS[k].some((hint) => label.includes(hint)),
    ) ?? null
  );
};

/** Colour for `<Typography color=...>` in the grids. */
export const STATUS_COLOR = {
  [STATUS_KIND.pending]: "orange",
  [STATUS_KIND.approved]: "green",
  [STATUS_KIND.rejected]: "error",
};

export const statusColorOf = (status) =>
  STATUS_COLOR[getStatusKind(status)] ?? "text.primary";

/** A request is still editable / cancellable only while it is pending. */
export const isPending = (row) => {
  const kind = getStatusKind(getRowStatus(row));
  // An unknown status is treated as pending so a request is never silently
  // locked out of its actions.
  return kind === null || kind === STATUS_KIND.pending;
};

/** Preselect the status matching the button the admin pressed. */
export const findStatusByKind = (statusList, kind) =>
  (statusList ?? []).find((status) => getStatusKind(status) === kind) ?? null;
